/**
 * A stable identity for the open document, independent of where (or whether) it is saved.
 *
 * Why not just `Office.context.document.url`: it is empty for any unsaved presentation/document,
 * so every unsaved file collapsed into one shared bucket, and a file's url changes the moment it
 * is saved or renamed. The id below is stored *inside* the file (Office document settings), so it
 * follows the document through Save/rename/move and exists before the file has ever been saved.
 *
 * Reading is side-effect free. The id is only written (which marks the document as modified) when
 * a run is started or joined, via ensureDocumentId().
 */

const ID_SETTING = 'qcbot.documentId';
const URL_SETTING = 'qcbot.documentIdUrl';

const isHttpUrl = (url: string) => /^https?:\/\//i.test(url);

function newId(): string {
  return [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** The id this document was given, or undefined if it has none yet (or is a different local file
 * than the one the id was issued for). */
export function readDocumentId(): string | undefined {
  const settings = Office.context.document?.settings;
  const id = settings?.get(ID_SETTING);
  if (typeof id !== 'string' || !id) return undefined;
  // "Save As" copies the settings into the new file. A different local path means this is another
  // file wearing the original's id, so it must not inherit the original's results. Cloud urls are
  // not compared: the same file reports different urls on desktop and web.
  const issuedFor = settings.get(URL_SETTING);
  const url = Office.context.document.url;
  if (typeof issuedFor === 'string' && issuedFor && url && !isHttpUrl(issuedFor) && !isHttpUrl(url) && issuedFor !== url) return undefined;
  return id;
}

/** Gives the open document an id if it has none, and persists it into the file. */
export async function ensureDocumentId(): Promise<string | undefined> {
  const existing = readDocumentId();
  if (existing) return existing;
  const settings = Office.context.document?.settings;
  if (!settings) return undefined;
  const id = newId();
  settings.set(ID_SETTING, id);
  settings.set(URL_SETTING, Office.context.document.url ?? '');
  // The id stays readable for this session even if saving fails (e.g. a read-only file).
  await new Promise<void>((resolve) => settings.saveAsync(() => resolve()));
  return id;
}

/**
 * Namespace for everything stored about the open document, or undefined when it cannot be told
 * apart from other documents — callers must then store nothing rather than share a bucket.
 * Documents without an id fall back to their url, which is how runs were keyed before ids existed.
 */
export function getDocumentScope(): string | undefined {
  const id = readDocumentId();
  if (id) return `id:${id}`;
  return Office.context.document?.url || undefined;
}
